import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { PopupService } from './popup.service';
import { UtilityService } from './utility.service';
import { UserClientService } from './user-client.service';
import { TutorScreeningService } from './tutor-screening.service';
import { CustomCookieServiceService } from './custom-cookie-service.service';
import { ConfirmDialogComponent } from '../standalone-components/common-dialogs/confirm/confirm-dialog.component';
import { TutorAvailabilityDialogComponent } from '../standalone-components/common-dialogs/tutor-availability-dialog/tutor-availability-dialog.component';
import { ScreeningUploadDialogComponent } from '../standalone-components/screening-upload-dialog/screening-upload-dialog.component';
import { ScreeningDocType } from '../microservice-clients/tutor-screening.model';
import { UserStateEnum } from '../microservice-clients/user';
import { TutorContractSigningState } from '../constants';
import { TutorAcademyService } from './tutor-academy.service';
import { TutorAcademyWelcomeDialogComponent } from '../standalone-components/tutor-academy/tutor-academy-welcome-dialog/tutor-academy-welcome-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class TutorInitService {

  private screeningDialogRef: MatDialogRef<ScreeningUploadDialogComponent> | null = null;

  constructor(
    private authService: AuthService,
    private popupService: PopupService,
    private utilityService: UtilityService,
    private userClientService: UserClientService,
    private screeningService: TutorScreeningService,
    private cookieService: CustomCookieServiceService,
    private router: Router,
    private translateService: TranslateService,
    private academyService: TutorAcademyService,
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkBsnContractOnNavigation();
      this.redirectIncompleteTutorOnNavigation();
    });
  }

  public runChecks(): void {
    // Defensive: callers should wait for authService.session, but the checks below
    // dereference userDetails and must never run without it.
    if (!this.authService.isTeacher() || !this.authService.userDetails) {
      return;
    }

    this.checkRegistrationAndBsn();
    this.checkAndOpenTimeslotDialog();
    this.checkContract();
    this.checkAndOpenAcademyWelcomeDialog();
  }

  private checkAndOpenAcademyWelcomeDialog(): void {
    const state = this.authService.userDetails?.state;
    if (state === UserStateEnum.New || state === UserStateEnum.Onboarding) {
      return;
    }

    if (this.academyService.isMandatoryCourseCompleted()) {
      return;
    }

    if (!this.shouldShowAcademyWelcomeDialog()) {
      return;
    }

    this.markAcademyWelcomeDialogAsShown();

    const dialogRef = this.popupService.openResponsiveDialog(TutorAcademyWelcomeDialogComponent);
    dialogRef.componentInstance.onClose.subscribe(() => dialogRef.close());
  }

  private checkRegistrationAndBsn(): void {
    if (this.isTutorRegistrationIncomplete()) {
      this.authService.redirectToTutorRegistration();
    } else {
      this.popupService.checkToOpenBsnDialog(this.authService.userDetails);
    }
  }

  private redirectIncompleteTutorOnNavigation(): void {
    if (!this.authService.isTeacher() || !this.isTutorRegistrationIncomplete() || this.router.url.includes('reg2')) {
      return;
    }
    this.authService.redirectToTutorRegistration();
  }

  private isTutorRegistrationIncomplete(): boolean {
    return this.authService.userDetails?.state === UserStateEnum.New
      && !this.authService.userDetails?.tutorDetails?.guidelineCheckmark;
  }

  private checkAndOpenTimeslotDialog(): void {
    if (this.authService.userDetails?.tutorDetails?.showTimeslotsPopup === 'true' &&
      this.utilityService.isDesktopRes()
    ) {
      this.openTimeslotDialog();
    }
  }

  private openTimeslotDialog(): void {
    const dialogRef = this.popupService.openResponsiveDialog(TutorAvailabilityDialogComponent);
    const dialog = dialogRef.componentInstance;
    dialog.onConfirm.subscribe(() => {
      dialogRef.close();
      this.markTimeslotDialogSeen();
      this.translateService.get('ROUTES.Misc_tutor_edit_profile').subscribe(route => {
        this.router.navigate([`/${route}`]);
      });
    });
    dialog.onClose.subscribe(() => {
      dialogRef.close();
      this.markTimeslotDialogSeen();
    });
  }

  private markTimeslotDialogSeen(): void {
    const user = {
      id: this.authService.userDetails.id,
      tutorDetails: {
        showTimeslotsPopup: 'false',
      }
    } as any;
    this.authService.updateUserDetails(user).subscribe();
  }

  private async checkContract(): Promise<void> {
    let contractInfo = this.authService.userDetails.tutorDetails?.contractInfo;
    if (!contractInfo) {
      contractInfo = await this.getTutorContractInfo();
      if (contractInfo) {
        const tutorDetails = this.authService.userDetails.tutorDetails ?? ({} as any);
        tutorDetails.contractInfo = contractInfo;
        this.authService.userDetails.tutorDetails = tutorDetails;
      }
    }

    if (!contractInfo?.signingState) return;

    const status = contractInfo.signingState;

    if (
      this.utilityService.isDE &&
      this.authService.userDetails?.state === UserStateEnum.Onboarding &&
      (status === TutorContractSigningState.Pending || status === TutorContractSigningState.Signed)
    ) {
      if (!(await this.passScreeningGate(status === TutorContractSigningState.Pending))) {
        return;
      }
    }

    if (status === TutorContractSigningState.Pending) {
      this.popupService.openBsnContractDialog(this.authService.userDetails);
    } else if (status === TutorContractSigningState.Declined || status === TutorContractSigningState.Expired) {
      this.openContractDialog(status);
    }
  }

  private async passScreeningGate(offerContractAfter: boolean): Promise<boolean> {
    const user = await firstValueFrom(this.authService.getUserDetails());
    const toUpload = this.screeningService.documentsRequiringUpload(user?.tutorDetails?.screening);
    if (toUpload.length) {
      this.openScreeningDialog(toUpload, offerContractAfter);
      return false;
    }
    return true;
  }

  private openScreeningDialog(docTypes: ScreeningDocType[], offerContractAfter: boolean): void {
    if (this.screeningDialogRef) return;
    this.screeningDialogRef = this.popupService.openResponsiveDialog(ScreeningUploadDialogComponent);
    const dialog = this.screeningDialogRef.componentInstance;
    dialog.docTypes = docTypes;
    dialog.onConfirm.subscribe(() => {
      this.screeningDialogRef?.close();
      if (offerContractAfter) {
        this.popupService.openBsnContractDialog(this.authService.userDetails);
      }
    });
    this.screeningDialogRef.afterClosed().subscribe(() => {
      this.screeningDialogRef = null;
    });
  }

  private checkBsnContractOnNavigation(): void {
    if (!this.utilityService.isNL || !this.authService.isTeacher()) return;
    if (
      this.authService.userDetails?.personalInfo?.hasPersonalRegistryNumber === 'false'
      && this.authService.userDetails?.tutorDetails?.contractInfo?.signingState === TutorContractSigningState.Pending
    ) {
      this.popupService.openBsnContractDialog(this.authService.userDetails);
    }
  }

  private async getTutorContractInfo() {
    return await this.userClientService.getTutorContractInfo(this.authService.user.id).toPromise();
  }

  private openContractDialog(signingState: string): void {
    if (!this.shouldShowContractDialog()) {
      return;
    }

    const dialog = this.popupService.openResponsiveDialog(ConfirmDialogComponent).componentInstance;
    dialog.showCloseButton = false;
    dialog.dialogTitle = 'texts.Tutor_Contract_Dialog_Title';

    if (signingState === TutorContractSigningState.Declined || signingState === TutorContractSigningState.Expired) {
      dialog.dialogText = 'texts.Tutor_Contract_Dialog_Declined_Expired_Description';
    }

    dialog.dialogConfirmButton = 'texts.Tutor_Contract_Dialog_Button';

    this.markContractDialogAsShown();
  }

  private shouldShowContractDialog(): boolean {
    const cookieName = this.getContractDialogCookieName();
    return !this.cookieService.hasKey(cookieName);
  }

  private markContractDialogAsShown(): void {
    const cookieName = this.getContractDialogCookieName();
    this.cookieService.put(cookieName, 'shown', { expires: 1, path: '/' });
  }

  private getContractDialogCookieName(): string {
    return `tutor_contract_dialog_shown_${this.authService.user?.id}`;
  }

  private shouldShowAcademyWelcomeDialog(): boolean {
    const cookieName = this.getAcademyWelcomeDialogCookieName();
    return !this.cookieService.hasKey(cookieName);
  }

  private markAcademyWelcomeDialogAsShown(): void {
    const cookieName = this.getAcademyWelcomeDialogCookieName();
    this.cookieService.put(cookieName, 'shown', { expires: 1, path: '/' });
  }

  private getAcademyWelcomeDialogCookieName(): string {
    return `tutor_academy_welcome_shown_${this.authService.user?.id}`;
  }
}
